import { Box, IconButton, Typography } from "@mui/material";
import Chart from "react-apexcharts";
import PushPinIcon from "@mui/icons-material/PushPin";
import ExportIcon from "@heroicons/react/24/solid/CircleStackIcon";
import useTranslation from "shared/helpers/hooks/useTranslation";
import ButtonLoader from "shared/ui/ButtonLoader";
import { CampaignSurveyTypeList } from "../CampaignDetails/questions/LeftSidebar/constants";
import { PinSurvey, UnPinSurvey } from "store/slicers/campaignDetail";
import toast from "react-hot-toast";
import { ISurveysStatisticsGridResponse } from "store/interfaces/campaignDetails";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";

const SurveyCharts = ({
  data,
  init,
  pinAllSurvey,
  areAllSurveysPinned,
  onExport,
  campaignName,
}) => {
  const dispatch = useAsyncDispatch();
  const t = useTranslation();
  const baseColors = [
    "#6366f1",
    "#f59e0b",
    "#ef4444",
    "#10b981",
    "#8b5cf6",
    "#f97316",
    "#06b6d4",
    "#84cc16",
  ];

  const containerStyle = {
    minHeight: "100vh",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  };

  const titleStyle = {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "8px",
    letterSpacing: "-0.025em",
  };

  const handlePinSurvey = async (survey: ISurveysStatisticsGridResponse) => {
    if (survey.isPinned) {
      const { meta } = await dispatch(
        UnPinSurvey({
          campaignID: survey.campaignID,
          surveyID: survey.surveyID,
        })
      );
      if (meta.requestStatus !== "fulfilled") {
        return;
      }
      toast.success(t("survey_unpinned"));
    } else {
      const { meta } = await dispatch(
        PinSurvey({ campaignID: survey.campaignID, surveyID: survey.surveyID })
      );
      if (meta.requestStatus !== "fulfilled") {
        return;
      }
      toast.success(t("survey_pinned"));
    }
    init();
  };

  return (
    <Box sx={containerStyle}>
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h1 style={titleStyle}>{campaignName}</h1>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ marginRight: 2 }}>
              <ButtonLoader
                variant="contained"
                onClick={onExport}
                isLoading={false}
                disabled={!data?.length}
                startIcon={<ExportIcon height={24} width={24} />}
              >
                <Typography>{t("export")}</Typography>
              </ButtonLoader>
            </Box>
            <IconButton onClick={pinAllSurvey}>
              <PushPinIcon color={areAllSurveysPinned ? "primary" : "action"} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: "24px",
          margin: "0 auto",
        }}
      >
        {data?.map((survey, index) => {
          const categories =
            survey.answerResponds?.map((a) => a.surveyAnswerText) || [];
          const values =
            survey.answerResponds?.map((a) => a.respondCount) || [];

          // guard against empty arrays
          const maxValue = values.length ? Math.max(...values) : 0;
          const totalResponses = values.reduce((sum, val) => sum + val, 0);

          const isHorizontal =
            survey.surveyTypeEnum === 2 || survey.surveyTypeEnum === 3;
          const color = baseColors[index % baseColors.length];

          const gradientColors = [color, `${color}99`];

          const chartOptions = {
            chart: {
              type: "bar",
              toolbar: { show: false },
              fontFamily: "'Inter', sans-serif",
              background: "transparent",
              animations: {
                enabled: true,
                easing: "easeinout",
                speed: 800,
              },
            },
            subtitle: {
              text: `Total responses: ${totalResponses}`,
              align: "left",
              offsetY: 30,
              style: {
                fontSize: "14px",
                color: "#64748b",
                fontWeight: "400",
              },
            },
            xaxis: {
              categories,
              labels: {
                style: { fontSize: "12px", colors: "#64748b" },
                maxHeight: 100,
                trim: true,
              },
              axisBorder: { show: false },
              axisTicks: { show: false },
            },
            yaxis: {
              min: 0,
              max: maxValue ? Math.max(20, Math.ceil(maxValue * 1.1)) : 20,
              title: {
                text: isHorizontal ? "Number of Responses" : "Response Count",
                style: {
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#475569",
                },
              },
              labels: {
                style: { fontSize: "12px", colors: "#64748b" },
              },
            },
            plotOptions: {
              bar: {
                borderRadius: 0,
                horizontal: isHorizontal,
                columnWidth: "60%",
                barHeight: "40%",
              },
            },
            fill: {
              type: "gradient",
              gradient: {
                type: isHorizontal ? "horizontal" : "vertical",
                gradientToColors: gradientColors,
                opacityFrom: 0.95,
                opacityTo: 0.7,
              },
            },
            colors: [color],
            dataLabels: {
              enabled: true,
              style: {
                fontSize: "13px",
                colors: ["#ffffff"],
                fontWeight: "600",
              },
              background: { enabled: false },
            },
            grid: {
              borderColor: "#e2e8f0",
              strokeDashArray: 3,
              yaxis: { lines: { show: true } },
            },
            tooltip: {
              enabled: true,
              theme: "light",
              y: {
                formatter: (val) => `${val} response${val !== 1 ? "s" : ""}`,
              },
              custom: ({ series, seriesIndex, dataPointIndex }) => {
                const value = series[seriesIndex][dataPointIndex];
                if (!totalResponses || !value) {
                  return `<div style="padding:8px;">0 responses</div>`;
                }
                const percentage = ((value / totalResponses) * 100).toFixed(1);
                return `
                  <div style="padding:12px; background:white; border-radius:8px; box-shadow:0 4px 12px rgba(0,0,0,0.15);">
                    <div style="font-weight:600; color:#1e293b; margin-bottom:4px;">
                      ${categories[dataPointIndex]}
                    </div>
                    <div style="color:#64748b; font-size:11px;">
                      ${value} responses (${percentage}%)
                    </div>
                  </div>
                `;
              },
            },
            legend: { show: false },
          };

          const chartSeries = [
            {
              name: "Responses",
              data: values,
            },
          ];

          const cardStyle = {
            background: "#ffffff",
            padding: "28px",
            borderRadius: "16px",
            boxShadow: "0 4px 24px rgba(0, 0, 0, 0.06)",
            border: "1px solid #f1f5f9",
            position: "relative",
            overflow: "hidden",
          };

          const accentStyle = {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${color} 0%, ${color}80 100%)`,
          };

          return (
            <Box key={survey.surveyID ?? index} sx={cardStyle}>
              <Box sx={accentStyle}></Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "600", color: "#1e293b" }}
                >
                  {survey.surveyText} (
                  {CampaignSurveyTypeList[survey.surveyTypeEnum]})
                </Typography>
                <IconButton onClick={() => handlePinSurvey(survey)}>
                  <PushPinIcon color={survey.isPinned ? "primary" : "action"} />
                </IconButton>
              </Box>

              <Chart
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={400}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SurveyCharts;
